import {
  ESpinnerSize,
  ESpinnerType,
} from "@/common/icons/icons-common/icons-loading-models";
import { ICONS_BUTTON } from "@/common/icons/icons-common/IconsButton";
import { LOADING_SPINNER } from "@/common/icons/icons-common/IconsLoading";
import { cn } from "@/utils/cn";
import { renderIfTrue, renderSwitch } from "@/utils/react-conditional";
import { useNavigationGuard } from "@/utils/use-navigation-guard";
import { defined, exists } from "@/utils/variable-evaluation";
import type { ViewTransitionDirection } from "@/utils/view-transition";
import React from "react";
import type { LinkProps, Location, Path, To } from "react-router";
import { Link, useLocation } from "react-router";
import { EButtonActionType, EButtonType } from "./button-models";
import { useRipple } from "./use-ripple";

type ILinkProps = React.ComponentClass<LinkProps>;
type IAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;
type IButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;
type IPropsUnion = ILinkProps | IAnchorProps | IButtonProps;

// <Link to>'s own shape. No function form: nothing here needs the current
// location to compute `to` - only `state` does (e.g. `backTo`), below.
export type IButtonTo = string | Partial<Path>;

// A plain value, or a function of the current location for state that's
// derived from it. IMPORTANT: a callback here needs BOTH the `location`
// param annotated (this type is a union with `unknown`, which absorbs it -
// TypeScript has no contextual type left to infer the param from, so it's
// an implicit `any` otherwise) AND an explicit return type annotation
// (TypeScript's excess-property check does not apply to a concise-body
// arrow function's return value when its type comes only from context, so
// a typo'd or stray key would otherwise pass silently). Both required:
// `(location: Location): T => (...)`. See index.test.tsx for a worked
// example.
export type IButtonState = unknown | ((location: Location) => unknown);

type IProps = {
  dataTest?: string; // used for integration tests
  dataId?: string; // used for debugging
  to?: IButtonTo | null; // some callers pass `null` (not just omit `to`) to mean "no link"
  viewTransition?: ViewTransitionDirection; // animates the route change (see utils/view-transition.ts)
  state?: IButtonState; // passed to <Link>'s own `state` prop, not nested inside `to`
  href?: string;
  target?: string;
  buttonType?: EButtonType;
  isLoading?: boolean;
  hideLoader?: boolean;
  isError?: boolean;
  isLoaded?: boolean;
  isDisabled?: boolean; // BEWARE: link properties 'to' and 'href' are still working even if disabled
  isActive?: boolean;
  icon?: React.ReactNode;
  additionalClasses?: string[];
  inlineStyle?: React.CSSProperties;
  children?: React.ReactNode;
};

type IPropTypes = IProps & IPropsUnion;

// Button.module.scss's per-buttonType visual variants (30+ of them, several
// with their own isActive/isError/isDisabled sub-states, plus a handful of
// modifier strings passed through `additionalClasses`) are pre-computed
// here into final class strings via plain JS/lookup tables, using the same
// booleans/props already available at the single call site that renders
// this button -- rather than relying on CSS compound classes
// (`.Button.GreenWide.isDisabled`, etc.) and Tailwind's generated-CSS
// source order to pick a winner between competing same-property utilities.
// This matches the original SCSS's specificity + source-order cascade
// outcome deterministically. `isActive` is *also* kept as a literal marker
// class (in addition to its computed visual classes) because TabButton's
// `.isActive + .TabButton { border-left-color }` genuinely needs a real
// CSS adjacent-sibling match, which only a literal class (not a JS prop)
// can give the *next* button in a list.

const BUTTON_BASE_CLASS =
  "relative text-center m-0 p-0 whitespace-nowrap cursor-pointer no-underline focus:outline-none active:outline-none";

// The 7 buttonTypes that get this specific disabled treatment instead of
// the generic one. Their own bg/text/border colors are suppressed inside
// getButtonTypeClass when isDisabled (see below) so this is the ONLY color
// class applied for them -- stacking it alongside the type's own always-on
// color utility would leave the winner up to Tailwind's generated-CSS
// order, which isn't guaranteed to match the original selector-specificity
// (`.Button.GreenWide.isDisabled` beats `.Button.GreenWide`) outcome.
const DISABLED_BG_TYPES = new Set<EButtonType>([
  EButtonType.GreenWide,
  EButtonType.BlueWide,
  EButtonType.RedWide,
  EButtonType.BluePill,
  EButtonType.GreenPill,
  EButtonType.GhostPill,
  EButtonType.HeaderMenuOption,
]);
// bg-border-neutral/text-inverse read as "washed out" in light mode (the
// bg is nearly page-white), but border-neutral's dark value is *lighter*
// than the dark page background, so the same classes flipped a muted
// disabled button into one that looked more prominent than an enabled
// one. Went transparent for a while instead, but a border-only pill with
// no `border` width utility ever added turned out invisible (see PR
// history), and even fixed it still read as barely-there on plain
// surfaces. bg-disabled-bg is the same subtle-fill token disabled text
// inputs/selects already use (see TextArea.tsx, select/index.tsx) — a
// step below `surface` in both themes without border-neutral's
// "too light in dark mode" problem. It does coincide with `surface-alt`
// in dark mode (the settings/delete-account page background), but the
// border edge still reads there since border-color's dark value is
// visibly lighter than both.
const DISABLED_BG_CLASS =
  "bg-disabled-bg text-text-muted border border-border-color";
const DISABLED_COLOR_OVERRIDE_TYPES = new Set<EButtonType>([
  ...DISABLED_BG_TYPES,
]);

const WIDE_GHOST_ICON_LEFT =
  "[margin-left:var(--btn-icon-ml,-28px)] w-6 h-6 flex items-center justify-center shrink-0";
const WIDE_ICON_LEFT =
  "-ml-7 w-6 h-6 flex items-center justify-center shrink-0";

// Types with a visible surface to give touch feedback on. Bare/icon/link/
// image types are left out: no surface to clip a ripple to.
const RIPPLE_TYPES = new Set<EButtonType>([
  EButtonType.GhostWide,
  EButtonType.RedWideOutline,
  EButtonType.GreenWide,
  EButtonType.BlueWide,
  EButtonType.RedWide,
  EButtonType.BluePill,
  EButtonType.GreenPill,
  EButtonType.GhostPill,
  EButtonType.SocialLogin,
  EButtonType.HeaderMenuOption,
  EButtonType.DesktopFilter,
  EButtonType.SwitchButton,
  EButtonType.TabButton,
  EButtonType.CompleteProfile,
  EButtonType.ListItem,
  EButtonType.ListItemDesktop,
  EButtonType.ChatItem,
  EButtonType.MenuItem,
]);

function getButtonTypeClass(
  buttonType: EButtonType | undefined,
  {
    isActive,
    isError,
    isDisabled,
    additionalClasses,
  }: {
    isActive: boolean;
    isError: boolean;
    isDisabled: boolean;
    additionalClasses: string[];
  }
) {
  const has = (name: string) => additionalClasses.includes(name);

  switch (buttonType) {
    case EButtonType.GhostWide:
      return "flex items-center justify-center gap-1 py-3 px-3.75 w-full h-11.25 text-body-lg font-medium leading-4.75 rounded-3xl bg-surface border border-border-color text-body-text";
    case EButtonType.RedWideOutline:
      return "flex items-center justify-center gap-1 py-3 px-3.75 w-full h-11.25 text-body-lg font-medium leading-4.75 rounded-3xl bg-surface border border-error text-error";
    case EButtonType.GreenWide:
    case EButtonType.BlueWide:
    case EButtonType.RedWide: {
      // text-text-inverse moved out of the shared structural string (it's
      // now only added in the enabled branch) so the disabled treatment's
      // own text color from DISABLED_BG_CLASS isn't fighting an
      // unconditional same-property class of equal specificity.
      const structural =
        "flex items-center justify-center gap-1 py-3.25 px-3.75 w-full h-11.25 text-body-lg font-semibold leading-4.75 rounded-3xl";
      if (isDisabled) return structural;
      return cn(structural, "text-text-inverse", {
        "bg-success": buttonType === EButtonType.GreenWide,
        "bg-primary": buttonType === EButtonType.BlueWide,
        "bg-error": buttonType === EButtonType.RedWide,
      });
    }
    case EButtonType.BluePill:
    case EButtonType.GreenPill: {
      const structural =
        "block py-2 px-3 rounded-3xl border text-small leading-4 font-bold uppercase";
      if (isDisabled) return structural;
      return cn(
        structural,
        "text-text-inverse",
        buttonType === EButtonType.BluePill
          ? "border-primary bg-primary"
          : "border-success bg-success"
      );
    }
    case EButtonType.GhostPill: {
      const structural =
        "inline-flex items-center justify-center py-2 px-3 rounded-3xl border text-small leading-4 font-bold uppercase";
      if (isDisabled) return structural;
      if (has("--blue")) return cn(structural, "border-primary text-primary");
      if (has("--green")) return cn(structural, "border-success text-success");
      if (has("--red"))
        return cn(structural, "border-error text-danger-action");
      if (has("--white"))
        return cn(structural, "border-border-inverse text-text-inverse");
      return cn(structural, "border-radio-border text-body-text");
    }
    case EButtonType.SocialLogin:
      return "flex items-center justify-center gap-2.5 w-full h-11.25 pt-3 px-0 pb-2.75 mb-2.5 rounded-3xl font-bold text-body-lg leading-4.25 bg-transparent border border-border-color text-text-strong shadow-[0px_2px_4px_rgba(0,0,0,0.1)] hover:text-text-strong hover:shadow-none hover:border hover:border-border-strong active:text-text-strong active:shadow-[inset_0px_2px_4px_rgba(0,0,0,0.1)]";
    case EButtonType.BasicLink:
      return "inline-flex items-center justify-center w-full h-full m-0 text-text-strong [transition:color_100ms]";
    case EButtonType.BlueLink:
      return isDisabled ? "font-semibold" : "text-primary font-semibold";
    case EButtonType.RedLink:
      return isDisabled ? "font-semibold" : "text-danger-action font-semibold";
    case EButtonType.WhiteLink:
      return isDisabled ? "font-semibold" : "text-text-inverse font-semibold";
    case EButtonType.HeaderMenuOption: {
      const structural =
        "font-semibold text-body leading-4.75 w-full py-3 px-0 rounded-3xl text-center uppercase";
      if (isDisabled) return structural;
      return cn(
        structural,
        "text-text-inverse",
        has("--danger") ? "bg-error" : "bg-primary"
      );
    }
    case EButtonType.Icon:
      // flex centering, not text-align (Preflight makes svg display:block, so text-align no longer centers it).
      // "--stack" opts into flex-col for callers with several plain-block
      // children (no inline-block of their own) that need to stack
      // vertically -- e.g. NoChatsBackground's emoji + title + subtitle.
      // Not the default: other multi-child callers (e.g. header-desktop's
      // avatar + chevron) rely on their *own* inline-block children
      // sitting side by side, which flex-col would also stack.
      return cn(
        "flex relative items-center justify-center w-full h-full text-current cursor-pointer",
        { "flex-col": has("--stack") }
      );
    case EButtonType.IconWithBackground:
      return "flex relative items-center justify-center w-10 h-10 text-text-inverse bg-overlay border border-overlay-control-border rounded-full [transform:translate3d(0,0,0)] cursor-pointer";
    case EButtonType.InstagramImage:
      return cn(
        "inline-block align-top relative w-[33.33%] pt-[33.33%] border border-surface bg-media-placeholder text-icon-muted overflow-hidden [&>img]:absolute [&>img]:top-0 [&>img]:left-1/2 [&>img]:w-full [&>img]:h-full [&>img]:object-cover [&>img]:object-top [&>img]:-translate-x-1/2",
        { "[&>form]:block!": has("childFormForceDisplayBlock") }
      );
    case EButtonType.ProfileListed:
      return "bg-surface-alt overflow-hidden block [transition:200ms_max-height_cubic-bezier(0,1,0,1)]";
    case EButtonType.ProfileListedError:
      return "block min-h-100 overflow-hidden";
    case EButtonType.Gallery:
      return cn(
        "absolute top-1/2 -translate-y-1/2 cursor-pointer outline-none z-2 w-1/5 h-2/5 tablet:w-20",
        {
          "left-0 text-left [&_img]:ml-3": has("Left"),
          "right-0 text-right [&_img]:mr-3": has("Right"),
        }
      );
    case EButtonType.MobileFilter: {
      // font-weight/text-color/text-size are picked as ONE of two mutually
      // exclusive strings (rather than layered conditionally) because a
      // plain `font-bold` utility and a `tablet:font-normal` utility both
      // set the same property -- Tailwind always groups responsive variants
      // after the base layer, so `tablet:font-normal` would otherwise beat
      // `font-bold` at >=768px regardless of isActive, which is backwards.
      const structural =
        "leading-4.25 py-2.5 px-0 tablet:leading-4.75 tablet:p-2.5";
      return isActive
        ? cn(
            structural,
            "text-body/[inherit] tablet:text-body-lg/[inherit] font-bold text-primary border-b border-b-primary"
          )
        : cn(
            structural,
            "text-body/[inherit] font-medium text-body-text tablet:text-body-lg/[inherit] tablet:font-normal tablet:text-body-text"
          );
    }
    case EButtonType.SwitchButton: {
      const structural =
        "font-medium text-body leading-[normal] pt-1 px-0 pb-1.25 border border-r-0 w-full min-h-7.5 inline-flex justify-center items-center first:rounded-l-3xl last:rounded-r-3xl last:border-r";
      return isActive
        ? cn(structural, "bg-primary text-text-inverse border-primary")
        : cn(
            structural,
            "text-body-text bg-surface border-border-color last:border-r-border-color"
          );
    }
    case EButtonType.TabButton: {
      const structural =
        "font-normal text-body leading-4.25 bg-surface pt-2.25 pb-2.25 border border-r-0 w-full first:rounded-l-3xl last:rounded-r-3xl last:border-r [.isActive+&]:border-l-primary";
      return isActive
        ? cn(structural, "text-primary font-bold border-primary pl-2 pr-2")
        : cn(
            structural,
            "text-text-muted border-border-color px-2.25 last:border-r-border-color"
          );
    }
    case EButtonType.DesktopFilter: {
      const structural =
        "rounded-3xl font-normal text-body leading-4.75 pt-2.5 px-5 pb-2.75 border";
      return isActive
        ? cn(structural, "bg-facebook text-text-inverse border-button-outline")
        : cn(structural, "text-title-color bg-surface border-border-color");
    }
    case EButtonType.ListItem:
      return "block w-full px-4 py-3.75 text-left text-body-lg leading-[normal] font-normal text-inherit";
    case EButtonType.ListItemDesktop:
      return cn(
        "block text-left w-full text-inherit pl-4 text-body-lg leading-5.5 font-normal py-2.5",
        isError ? "pr-8" : "pr-4"
      );
    case EButtonType.ChatItem: {
      // ChatItem's "active" state comes through additionalClasses (a
      // literal "isActive" string from chatListItemButtonAdditionalClasses),
      // not the Button component's own isActive prop -- ChatsItem.tsx never
      // passes that prop to this particular Button.
      const chatIsActive = has("isActive");
      const isUnread = has("isUnread");
      const isSupportChat = has("isSupportChat");
      const isSelected = has("isSelected");
      const isEditing = has("isEditing");
      // Priority mirrors the original selectors' specificity/source order:
      // .isActive (3 classes) > .isEditing.isDisabled (5 class-selectors,
      // incl. :not(.isActive)) > .isUnread.isSupportChat (5, later in
      // source) > .isUnread / .isSelected (4).
      let bg = "";
      if (chatIsActive) {
        bg = "bg-primary text-text-inverse";
      } else if (isEditing && isDisabled) {
        bg = "bg-surface-alt";
      } else if (isUnread && isSupportChat) {
        bg = "bg-light-blue";
      } else if (isUnread || isSelected) {
        bg = "bg-light-blue";
      }
      return cn(
        "absolute top-0 left-0 text-left w-full h-17.5 text-title-color",
        bg
      );
    }
    case EButtonType.MenuItem:
      return "block w-full h-full p-5 text-center";
    case EButtonType.ImageGalleryDot:
      return "relative w-2.5 h-2.5";
    case EButtonType.CompleteProfile:
      return "block py-3.5 px-3.75 w-full text-body-lg leading-4.75 rounded-3xl bg-primary text-text-inverse tablet:inline-block tablet:py-3 tablet:px-11 tablet:font-semibold tablet:w-auto tablet:float-right tablet:text-body tablet:leading-4.75 tablet:rounded-3xl tablet:bg-primary tablet:text-text-inverse tablet:shadow-[0_2px_4px_rgba(0,0,0,0.1)]";
    default:
      // NoStyle / BlackGhost / undefined: no dedicated rule in the
      // original Button.module.scss either -- base classes only.
      return "";
  }
}

function getIconLeftClass(buttonType: EButtonType | undefined) {
  switch (buttonType) {
    case EButtonType.GhostWide:
    case EButtonType.RedWideOutline:
      return WIDE_GHOST_ICON_LEFT;
    case EButtonType.GreenWide:
    case EButtonType.BlueWide:
    case EButtonType.RedWide:
      return WIDE_ICON_LEFT;
    default:
      return "";
  }
}

function getLoadingIconClass(
  buttonType: EButtonType | undefined,
  additionalClasses: string[]
) {
  const classes = ["absolute right-2.5 top-1/2 w-6 h-6 -translate-y-1/2"];
  switch (buttonType) {
    case EButtonType.BlueLink:
    case EButtonType.RedLink:
    case EButtonType.WhiteLink:
      classes.push("-right-7");
      break;
    case EButtonType.ListItem:
      classes.push("right-4");
      break;
  }
  if (additionalClasses.includes("--narrow")) {
    classes.push("w-full h-full left-0");
  }
  return cn(classes);
}

function getErrorIconClass(buttonType: EButtonType | undefined) {
  return cn(
    "absolute right-2.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 [&_svg]:align-baseline",
    {
      "right-4": buttonType === EButtonType.ListItem,
    }
  );
}

export const Button = React.memo(function Button(props: IPropTypes) {
  // Only used to resolve a function-form `state` - `to` has no function
  // form (see IButtonTo above).
  const location = useLocation();

  // This is the app's one wired-up entry point for
  // navigation-blocker-aware navigation - see src/utils/navigation-guard.tsx
  // for why here specifically. The click handler below always takes
  // ownership of a processable click (preventDefault, then
  // attemptNavigate) rather than only on the blocked branch, matching
  // <Link>'s own documented onClick-composition contract (it calls a
  // consumer onClick first and checks `event.defaultPrevented` before
  // running its own internal navigate) - see the handler itself for why
  // that matters here.
  const { attemptNavigate } = useNavigationGuard();

  const { layerRef, onPointerDown: onRipplePointerDown } =
    useRipple<HTMLElement>(
      !!props.buttonType &&
        RIPPLE_TYPES.has(props.buttonType) &&
        !props.isDisabled &&
        !props.isLoading
    );

  const renderContent = (additionalClasses: string[]) => {
    const { icon, isLoading, hideLoader, isError, isLoaded, children } = props;
    const iconContent = icon
      ? isLoading
        ? LOADING_SPINNER(ESpinnerSize.ExtraSmall)[ESpinnerType.Ios]
        : icon
      : null;
    return (
      <>
        <span
          ref={layerRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
        />
        {icon && (
          <span className={getIconLeftClass(props.buttonType)}>
            {iconContent}
          </span>
        )}
        {!icon &&
          renderIfTrue(!!isLoading && !hideLoader, () => (
            <div
              className={getLoadingIconClass(
                props.buttonType,
                additionalClasses
              )}
            >
              {LOADING_SPINNER(ESpinnerSize.Small)[ESpinnerType.Ios]}
            </div>
          ))}
        {renderIfTrue(!!isError, () => (
          <div className={getErrorIconClass(props.buttonType)}>
            {ICONS_BUTTON.Error}
          </div>
        ))}
        {renderIfTrue(!!isLoaded, () => (
          <div
            className={getLoadingIconClass(props.buttonType, additionalClasses)}
          >
            {ICONS_BUTTON.Check}
          </div>
        ))}
        {children}
      </>
    );
  };

  const action = (): string => {
    if (defined(props.to)) {
      return EButtonActionType.InternalLink;
    } else if (defined(props.href)) {
      return EButtonActionType.ExternalLink;
    } else {
      return EButtonActionType.NormalButton;
    }
  };

  const renderButton = () => {
    const {
      dataTest,
      dataId,
      isDisabled,
      isActive,
      isLoading,
      hideLoader,
      isError,
      isLoaded,
      buttonType,
      icon,
      to,
      state,
      viewTransition,
      href,
      target,
      children,
      additionalClasses: rawAdditionalClasses,
      inlineStyle,
      ...restProps
    } = props;
    const { onPointerDown: onPointerDownProp, ...remainingProps } =
      restProps as IButtonProps;
    const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
      onRipplePointerDown(event);
      onPointerDownProp?.(event as React.PointerEvent<HTMLButtonElement>);
    };

    const additionalClasses = (rawAdditionalClasses || []).filter((c) =>
      exists(c)
    );

    // ChatItem's own isActive (bg-primary text-text-inverse, driven by
    // additionalClasses, see getButtonTypeClass) has higher CSS specificity
    // than the generic disabled treatment in the original SCSS, so the
    // generic grey/cursor text color is skipped in that one case to avoid
    // an ambiguous Tailwind stacking order between the two.
    const chatItemIsActiveWhileDisabled =
      buttonType === EButtonType.ChatItem &&
      additionalClasses.includes("isActive");

    const buttonClass = cn(
      BUTTON_BASE_CLASS,
      {
        isActive: !!isActive, // literal marker: TabButton's adjacent-sibling rule
        "cursor-not-allowed": !!isDisabled,
        "text-text-muted":
          !!isDisabled &&
          !DISABLED_COLOR_OVERRIDE_TYPES.has(buttonType!) &&
          !chatItemIsActiveWhileDisabled,
        [DISABLED_BG_CLASS]: !!isDisabled && DISABLED_BG_TYPES.has(buttonType!),
      },
      getButtonTypeClass(buttonType, {
        isActive: !!isActive,
        isError: !!isError,
        isDisabled: !!isDisabled,
        additionalClasses,
      }),
      {
        underline: additionalClasses.includes("--underlined"),
        "rounded-[46px]": additionalClasses.includes("--rounded"),
        "inline-block h-auto w-auto": additionalClasses.includes("--inline"),
        "py-1.5 px-1.75": additionalClasses.includes("--narrow"),
        "animate-[jiggle_4s_infinite_4s]":
          additionalClasses.includes("--jiggle"),
      }
    );
    // `to` is never undefined in the InternalLink branch below - action()
    // already gated on `defined(props.to)` - so this cast is safe.
    // `to` is never null/undefined in the InternalLink branch below -
    // action() already gated on `defined(props.to)` - so this cast is safe.
    const resolvedTo = to as To;
    const resolvedState = typeof state === "function" ? state(location) : state;

    // Pulled out of remainingProps so it can be composed with the guard
    // check below instead of being overwritten by it (or overwriting it,
    // depending on prop order).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- This polymorphic third-party/component boundary requires a broader contract migration.
    const { onClick: onClickProp, ...linkProps } = remainingProps as any;

    return renderSwitch(action(), {
      [EButtonActionType.InternalLink]: () => (
        <Link
          data-test={dataTest}
          data-id={dataId}
          to={resolvedTo}
          state={resolvedState}
          className={buttonClass}
          role="button"
          style={inlineStyle}
          onPointerDown={onPointerDown}
          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
            onClickProp?.(event);
            if (event.defaultPrevented) {
              return;
            }
            // Only take over the same primary, unmodified clicks <Link>
            // itself would act on - mirrors its own
            // shouldProcessLinkClick/isModifiedEvent checks, so
            // Ctrl/Cmd/Shift-click, middle-click, and target!="_self"
            // keep their native browser behavior (e.g. open in a new
            // tab) untouched by the guard.
            const isProcessableClick =
              event.button === 0 &&
              (!target || target === "_self") &&
              !(
                event.metaKey ||
                event.altKey ||
                event.ctrlKey ||
                event.shiftKey
              );
            if (
              isProcessableClick &&
              (typeof resolvedTo === "string" ||
                (typeof resolvedTo === "object" && resolvedTo !== null))
            ) {
              // Always take ownership of a processable click before
              // deciding, rather than only calling preventDefault() on
              // the blocked branch: <Link>'s own internal click handler
              // runs right after ours (its documented onClick-composition
              // contract - see the comment above useNavigationGuard()'s
              // import), and would otherwise navigate() a second time
              // when attemptNavigate already performed the transition
              // immediately (guard inactive), duplicating the history
              // entry.
              event.preventDefault();
              attemptNavigate(
                "PUSH",
                resolvedTo,
                resolvedState,
                viewTransition
              );
            }
          }}
          {...linkProps}
        >
          {renderContent(additionalClasses)}
        </Link>
      ),
      [EButtonActionType.ExternalLink]: () => (
        <a
          data-test={dataTest}
          data-id={dataId}
          key={`a-${href}`}
          href={href}
          className={buttonClass}
          role="button"
          target={`${target || "_blank"}`}
          style={inlineStyle}
          onPointerDown={onPointerDown}
          {...(remainingProps as IAnchorProps)}
        >
          {renderContent(additionalClasses)}
        </a>
      ),
      [EButtonActionType.NormalButton]: () => (
        <button
          data-test={dataTest}
          data-id={dataId}
          key="button"
          className={buttonClass}
          disabled={isDisabled || isLoading}
          style={inlineStyle}
          onPointerDown={onPointerDown}
          {...(remainingProps as IButtonProps)}
        >
          {renderContent(additionalClasses)}
        </button>
      ),
    });
  };

  return renderButton();
});
